/**
 * Guide language config client (#780, moved into user_guides at the v3
 * cutover #1029).
 *
 * `languages.json` is published to the shared guide-content bucket by
 * publish-guide.py - it lists every locale a guide is available in and the
 * Polly voice(s) per gender. The frontend reads it to build the Language menu
 * and to know which narration genders a locale supports.
 *
 * Shape: { "<LanguageCode>": { "label": string, "voices": { female?, male? } } }
 */

import { guideCdnBaseUrl } from '../loader';
import { ensureCdnCookies } from '../cdnAuth';

/**
 * Fetch the published language config from the guide CDN.
 * @returns {Promise<Object>} LanguageCode → { label, voices }
 */
export const fetchLanguageConfig = async () => {
  const base = guideCdnBaseUrl();
  const doFetch = async () => {
    try {
      return await fetch(`${base}/languages.json`, { credentials: 'include' });
    } catch (err) {
      throw new Error(`language config fetch failed: ${err.message}`);
    }
  };
  await ensureCdnCookies();
  let res = await doFetch();
  if (res.status === 403) {
    await ensureCdnCookies({ force: true });
    res = await doFetch();
  }
  if (!res.ok) throw new Error(`language config fetch failed: ${res.status}`);
  const config = await res.json();
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    throw new Error('language config is not an object');
  }
  return config;
};

/**
 * Narration genders a locale supports, derived from its config entry.
 * Falls back to female+male when the config is unavailable.
 * @param {Object} languageConfig
 * @param {string} locale
 * @returns {string[]}
 */
export const availableGenders = (languageConfig, locale) => {
  const entry = languageConfig && languageConfig[locale];
  const voices = entry && entry.voices;
  if (voices && typeof voices === 'object') {
    const genders = Object.keys(voices);
    if (genders.length > 0) return genders;
  }
  return ['female', 'male'];
};
